import {
  Card,
  DiffViewerContentCols,
  DiffViewerTitleCols,
  Row,
} from '@/components';
import { useMinistries } from '@/features/master-table/hooks';
import { usePolicies } from '@/features/master-table/policy/_hooks/policy';
import { diffRenderValueFormatter } from '@/features/project/_utils/diff-render-value-formatter';
import { formatLegalBasis } from '@/features/project/_utils/legal-bases';
import { useOrganizations } from '@/features/tenant-manager/organization/_hooks/organization';
import { useFilteredUsers } from '@/features/tenant-manager/user/_hooks/user';
import type { DiffViewConfigType } from '@/types/diff-view-config';
import {
  ITEM_SPLITTER,
  NEST_ITEM_CONNECTOR,
  findUpperRecursive,
} from '@/utils';
import {
  ImplementationMethodLabelModifier,
  ProjectCategoryLabelModifier,
  SheetTypeLabelModifier,
} from '@/utils/enum';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  ProjectRelationTypeLabelModifier,
  type ReviewSheetModel,
} from '../../../_models/project';

type Project = z.infer<typeof ReviewSheetModel>;

type Props = {
  sourceProject: Project; // left side
  targetProject: Project; // right side
};

type DiffViewConfigItem = DiffViewConfigType<Project>;

export const RsProjectDiffView: FC<Props> = ({
  sourceProject,
  targetProject,
}) => {
  const { t } = useTranslation();
  const organizations = useOrganizations();
  const policies = usePolicies();
  const users = useFilteredUsers({
    params: {
      filterBy: 'ids',
      ids: [
        ...new Set([
          ...(sourceProject.peopleInChargeIds ?? []),
          ...sourceProject.businessManagerIds,
          ...(targetProject.peopleInChargeIds ?? []),
          ...targetProject.businessManagerIds,
        ]),
      ],
      showDeleted: true,
    },
  });
  const ministries = useMinistries();

  const diffViewConfig: DiffViewConfigItem[] = [
    {
      prop: 'sheetType',
      label: t('sheet-type'),
      converter: (value: Project['sheetType']) =>
        [
          SheetTypeLabelModifier[value],
          t('need-administrative-reform-team-evaluation'),
        ].join(ITEM_SPLITTER),
    },
    {
      prop: 'ministryId',
      label: t('ministry'),
      converter: (value: Project['ministryId']) =>
        diffRenderValueFormatter.ministryId(value, ministries.data),
    },
    {
      prop: 'organizationId',
      label: t('organization-in-charge'),
      converter: (value: Project['organizationId']) =>
        findUpperRecursive(value, organizations.data ?? [])
          .reverse()
          .map((d) => d.name)
          .join(NEST_ITEM_CONNECTOR),
    },
    {
      prop: 'peopleInChargeIds',
      label: t('people-in-charge'),
      converter: (value: Project['peopleInChargeIds']) =>
        users.data?.results
          .flatMap((user) => (value?.includes(user.id) ? [user.name] : []))
          .join('\n') ?? '',
    },
    {
      prop: 'businessManagerIds',
      label: t('business-manager'),
      converter: (value: Project['businessManagerIds']) =>
        users.data?.results
          .flatMap((user) => (value.includes(user.id) ? [user.name] : []))
          .join('\n') ?? '',
    },
    {
      prop: 'otherResponsibleOrganizationIds',
      label: t('other-organization-in-charge'),
      converter: (values: Project['otherResponsibleOrganizationIds']) =>
        values
          .map((value) =>
            findUpperRecursive(value, organizations.data ?? [])
              .reverse()
              .map((d) => d.name)
              .join(NEST_ITEM_CONNECTOR),
          )
          .join('\n'),
    },
    {
      prop: 'relatedOrganizationIds',
      label: t('related-organizations'),
      converter: (values: Project['relatedOrganizationIds']) =>
        values
          .map((value) =>
            findUpperRecursive(value, organizations.data ?? [])
              .reverse()
              .map((d) => d.name)
              .join(NEST_ITEM_CONNECTOR),
          )
          .join('\n'),
    },
    { prop: 'projectNumber', label: t('project-number') },
    { prop: 'name', label: t('project-name') },
    { prop: 'fiscalYear', label: t('fiscal-year') },
    {
      prop: 'category',
      label: t('project-category'),
      converter: (value: Project['category']) =>
        value ? ProjectCategoryLabelModifier[value] : '',
    },
    {
      prop: 'startYear',
      label: t('start-fiscal-year-of-project'),
      converter: (_, project) =>
        project?.startYear?.toString() ??
        (project?.isUnknownStart ? t('unknown-start') : t('blank')),
    },
    {
      prop: 'endYear',
      label: t('end-fiscal-year-of-project'),
      converter: (_, project) =>
        project?.endYear?.toString() ??
        (project?.isNoScheduledEnd ? t('no-scheduled-end') : t('blank')),
    },
    {
      prop: 'policiesForProject',
      label: t('policy'),
      converter: (value: Project['policiesForProject']) =>
        value
          .map((v) => {
            const policy = findUpperRecursive(v.policyId, policies.data ?? [])
              .reverse()
              .map((d) => d.name)
              .join(NEST_ITEM_CONNECTOR);
            return `${policy}${ITEM_SPLITTER}${v.policyUrl ?? ''}`;
          })
          .join('\n'),
      isLongField: true,
    },
    {
      prop: 'relatedProjects',
      label: t('related-projects'),
      converter: (value: Project['relatedProjects']) =>
        value
          .map(
            (relatedProject) =>
              `${ProjectRelationTypeLabelModifier[relatedProject.relationType]}: ${relatedProject.name}`,
          )
          .join('\n'),
    },
    {
      prop: 'mainCosts',
      label: t('main-cost'),
      converter: (value: Project['mainCosts']) =>
        value.map((cost) => cost.name).join(ITEM_SPLITTER),
    },
    { prop: 'purpose', label: t('project-purpose'), isLongField: true },
    { prop: 'task', label: t('project-task'), isLongField: true },
    { prop: 'overview', label: t('project-overview'), isLongField: true },
    { prop: 'overviewUrl', label: t('overview-url') },
    {
      prop: 'legalBases',
      label: t('legal-bases'),
      converter: (value: Project['legalBases']) =>
        value?.map(formatLegalBasis).join('\n') ?? '',
    },
    {
      prop: 'plans',
      label: t('plan'),
      isLongField: true,
      converter: (value: Project['plans']) =>
        (value ?? [])
          .map((v) => `${v.contents ?? ''}\n${v.url ?? ''}`)
          .join('\n'),
    },
    {
      prop: 'subsidyRates',
      label: t('subsidy-rate'),
      converter: (values: Project['subsidyRates']) =>
        (values ?? [])
          .map((value) =>
            [
              value.target ?? '',
              value.ratesText ?? '',
              value.limitText ?? '',
              value.url ?? '',
            ].join(ITEM_SPLITTER),
          )
          .join('\n'),
      isLongField: true,
    },
    {
      prop: 'implementationMethods',
      label: t('implementation-method'),
      converter: (value: Project['implementationMethods']) =>
        (value ?? [])
          .map((v) => ImplementationMethodLabelModifier[v])
          .join(ITEM_SPLITTER),
    },
    { prop: 'sortOrder', label: t('project-order-number') },
    { prop: 'remark', label: t('remark'), isLongField: true },
  ];

  return (
    <Card>
      <Row gutter={[8, 0]}>
        <DiffViewerTitleCols label={t('project')} />
        {diffViewConfig.map(({ prop, label, converter, isLongField }) => (
          <DiffViewerContentCols<Project>
            key={`project-${prop}`}
            legend={label}
            prop={prop}
            textConverter={converter}
            isLongField={isLongField}
            source={sourceProject}
            target={targetProject}
          />
        ))}
      </Row>
    </Card>
  );
};
